import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import GroupedApplicationsCard from '../ui/GroupedApplicationsCard.tsx';
import debounce from 'lodash.debounce';
import { applicationIdentityKey } from '../../lib/councils';
import {
  getVirtualRowTranslateY,
  GRID_VIRTUALIZER_OVERSCAN_ROWS,
} from '../../lib/planning/feedPerformance';

/**
 * A grid view following the Neo-Civic Noir design system
 */
const SimpleGridView = ({
  groupedArray,
  showAiSummaries,
  selectedImage,
  setSelectedImage,
  expandedIndex,
  setExpandedIndex,
  cardHoverVariants,
  favorites,
  toggleFavorite
}) => {
  /** -------------------------------------------
   * Responsive column calculation
   * ----------------------------------------- */
  const [columns, setColumns] = useState(3);
  const virtualListRef = useRef(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    const handleResize = debounce(() => {
      const width = window.innerWidth;
      if (width >= 1280) setColumns(3);
      else if (width >= 1024) setColumns(2);
      else if (width >= 768) setColumns(2);
      else setColumns(1);
    }, 150);

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  /** -------------------------------------------
   * Pre-compute rows (array of arrays) 
   * so that each row contains up to `columns` groups
   * ----------------------------------------- */
  const rows = useMemo(() => {
    const arr = [];
    for (let i = 0; i < groupedArray.length; i += columns) {
      arr.push(groupedArray.slice(i, i + columns));
    }
    return arr;
  }, [groupedArray, columns]);

  // The grid starts below a large filter workspace. Window virtualization must
  // subtract that document offset or it will skip rows before they reach view.
  useEffect(() => {
    const node = virtualListRef.current;
    if (!node) return;
    const nextScrollMargin = node.getBoundingClientRect().top + window.scrollY;
    setScrollMargin((current) => (
      Math.abs(current - nextScrollMargin) > 1 ? nextScrollMargin : current
    ));
  });

  /** -------------------------------------------
   * Virtualizer setup (row-level)
   * ----------------------------------------- */
  const rowVirtualizer = useWindowVirtualizer({
    count: rows.length,
    scrollMargin,
    estimateSize: () => 460, // Approx height of a card including margin – tweak if needed
    // Correct document offsets make a small buffer sufficient without
    // mounting another viewport of stateful cards.
    overscan: GRID_VIRTUALIZER_OVERSCAN_ROWS,
  });

  return (
    <div className="w-full pb-8 px-4 sm:px-6 md:px-8 max-w-7xl mx-auto neo-civic-grid">
      <div
        ref={virtualListRef}
        style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative' }}
      >
        {rowVirtualizer.getVirtualItems().map((virtualRow) => {
          const row = rows[virtualRow.index];
          return (
            <div
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={rowVirtualizer.measureElement}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 md:gap-8 mb-8 w-full absolute left-0 top-0 py-2 sm:py-4"
              style={{
                transform: `translateY(${getVirtualRowTranslateY(virtualRow.start, scrollMargin)}px)`,
                width: '100%',
              }}
            >
              {row.map((group, colIndex) => {
                const globalIndex = virtualRow.index * columns + colIndex;
                const primaryApp = group.apps[0] || {};
                const primaryIdentity = applicationIdentityKey(primaryApp);
                const isFavorite = favorites?.some(
                  fav => applicationIdentityKey(fav || {}) === primaryIdentity
                );
                const isExpanded = expandedIndex === globalIndex;

                return (
                  <div
                    key={primaryIdentity || globalIndex}
                    className="w-full transform transition-transform duration-150 active:scale-[0.98]"
                  >
                    <GroupedApplicationsCard
                      group={group}
                      showAiSummaries={showAiSummaries}
                      selectedImage={selectedImage}
                      setSelectedImage={setSelectedImage}
                      isExpanded={isExpanded}
                      onToggleExpand={() => {
                        if (expandedIndex === globalIndex) {
                          setExpandedIndex(null);
                        } else {
                          setExpandedIndex(globalIndex);
                        }
                      }}
                      cardHoverVariants={cardHoverVariants}
                      isFavorite={isFavorite}
                      onToggleFavorite={() => toggleFavorite && toggleFavorite(primaryApp)}
                    />
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>

      {/* Subtle loading indicator at bottom of grid to ensure scroll events are triggered */}
      <div className="w-full py-8 mt-2 flex justify-center">
        <div className="h-1 w-1/2 rounded-full bg-[var(--c-border)] opacity-50"></div>
      </div>
    </div>
  );
};

export default React.memo(SimpleGridView);
